import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAITaskRegistration } from "../lib/aiTasks";
import {
  NotePencil, FilePlus, Trash, ArrowLeft, Sparkle, PaperPlaneTilt,
  CheckCircle, Info, Lock,
} from "@phosphor-icons/react";

/**
 * Verslaglegging — coach-only feature on the coachee detail page.
 *
 * View modes:
 *  - "list": all reports for this coachee + button to start a new draft.
 *  - "chat": chatbot drafting / refining a single report.
 *  - "view": read a finalised report.
 *
 * The activity log is derived server-side from existing timestamps.
 */
export default function VerslagleggingCoachPanel({ coacheeId, coacheeName }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("list"); // list | chat | view
  const [activeReport, setActiveReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/coach-reports`);
      setReports(data.reports || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [coacheeId]);

  const handleStart = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/coachees/${coacheeId}/coach-reports`, {});
      setActiveReport(data);
      setView("chat");
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleOpen = async (r) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.get(`/coachees/${coacheeId}/coach-reports/${r.id}`);
      setActiveReport(data);
      // Anything with a markdown report opens in viewer; pure draft (no
      // markdown yet) opens in chat to continue the conversation.
      setView((data.report_markdown || "").trim() ? "view" : "chat");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Verslag "${r.title || "concept"}" verwijderen? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await api.delete(`/coachees/${coacheeId}/coach-reports/${r.id}`);
      if (activeReport?.id === r.id) { setActiveReport(null); setView("list"); }
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (view === "chat" && activeReport) {
    return (
      <ReportChat
        coacheeId={coacheeId}
        coacheeName={coacheeName}
        report={activeReport}
        onChange={(r) => setActiveReport(r)}
        onBack={async () => { setView("list"); setActiveReport(null); await load(); }}
        onReportReady={async (r) => { setActiveReport(r); setView("view"); await load(); }}
      />
    );
  }
  if (view === "view" && activeReport) {
    return (
      <ReportView
        coacheeId={coacheeId}
        report={activeReport}
        onChange={(r) => setActiveReport(r)}
        coacheeName={coacheeName}
        onBack={() => { setView("list"); setActiveReport(null); load(); }}
        onReopen={() => setView("chat")}
        onDelete={() => handleDelete(activeReport)}
      />
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="coach-reports-panel">
      <div className="neuro-card-bold p-5 bg-white">
        <div className="flex items-start gap-3">
          <NotePencil size={26} weight="duotone" className="text-[#1E88E5] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold tracking-tight">Verslaglegging</h3>
            <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
              Maak een verslag over {coacheeName || "deze coachee"} met behulp van AI. De AI vraagt
              eerst uit wat voor verslag je wilt en gebruikt vervolgens een activiteiten­log
              (sinds het vorige verslag) om een concreet verslag op te stellen.
            </p>
            <button
              type="button"
              onClick={handleStart}
              disabled={busy}
              data-testid="coach-reports-new-btn"
              className="mt-3 inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
            >
              <FilePlus size={16} /> {busy ? "Bezig…" : "Nieuw verslag"}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="coach-reports-error">{error}</div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Laden…</div>
      ) : reports.length === 0 ? (
        <div className="neuro-card p-6 bg-white" data-testid="coach-reports-empty">
          <div className="flex items-start gap-3">
            <Info size={22} className="text-[#1E88E5] shrink-0 mt-0.5" />
            <div>
              <h4 className="font-display text-base font-bold tracking-tight">Nog geen verslagen</h4>
              <p className="text-sm text-zinc-600 mt-1">
                Klik op &ldquo;Nieuw verslag&rdquo; om er eentje te maken.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="coach-reports-list">
          {reports.map((r) => (
            <ReportListItem
              key={r.id}
              report={r}
              onOpen={() => handleOpen(r)}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportListItem({ report, onOpen, onDelete }) {
  const dt = new Date(report.finalized_at || report.concept_at || report.created_at).toLocaleString();
  const status = report.status; // drafting | concept | finalized
  const badge = (() => {
    if (status === "finalized") return { label: "Definitief", cls: "bg-[#43A047]/10 text-[#43A047] border border-[#43A047]/30" };
    if (status === "concept") return { label: "Concept", cls: "bg-amber-100 text-amber-800 border border-amber-300" };
    return { label: "In gesprek", cls: "bg-zinc-100 text-zinc-700 border border-zinc-300" };
  })();
  return (
    <div className="neuro-card p-4 bg-white" data-testid={`coach-report-row-${report.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={onOpen}
          data-testid={`coach-report-open-${report.id}`}
          className="text-left flex-1 min-w-[200px]"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-base font-bold tracking-tight">
              {report.title || (status === "drafting" ? "Nieuw concept" : "Verslag")}
            </div>
            <span className={"inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide rounded-sm " + badge.cls}>
              {badge.label}
            </span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">{dt} · door {report.coach_name || "—"}</div>
        </button>
        {status !== "finalized" && (
          <button
            type="button"
            onClick={onDelete}
            data-testid={`coach-report-delete-${report.id}`}
            aria-label="Verwijder verslag"
            className="p-2 text-zinc-500 hover:text-[#E53935] hover:bg-[#E53935]/5 rounded-sm transition-colors"
          >
            <Trash size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function ReportChat({ coacheeId, coacheeName, report, onChange, onBack, onReportReady }) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Global AI-task pill: shows a persistent badge in the bottom-right
  // even if the coach navigates away to another tab mid-generation.
  useAITaskRegistration({
    active: generating,
    id: `coach-report-${coacheeId}-${report?.id || "draft"}`,
    label: `Verslag opstellen voor ${coacheeName || "coachee"}`,
    returnTo: `/coachees/${coacheeId}`,
  });

  useEffect(() => {
    if (!scrollRef.current) return;
    const last = scrollRef.current.lastElementChild;
    if (last) {
      const containerTop = scrollRef.current.getBoundingClientRect().top;
      const elTop = last.getBoundingClientRect().top;
      scrollRef.current.scrollTop += elTop - containerTop;
    }
  }, [report?.messages?.length]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Heuristic — when the coach uses a clear affirmative, the AI will
  // produce the full report. We flip a "generating" flag so the UI shows
  // a big loading overlay while the long LLM call finishes.
  const looksLikeAffirmative = (s) => {
    const t = (s || "").toLowerCase().trim();
    if (!t) return false;
    const triggers = ["ja", "genereer", "maak het", "schrijf het", "doe maar", "akkoord", "prima", "ok", "oké", "graag"];
    return triggers.some((w) => t.split(/[\s.,!?]+/).includes(w) || t.startsWith(w));
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;
    const willGenerate = looksLikeAffirmative(text);
    setSending(true);
    if (willGenerate) setGenerating(true);
    setError("");
    try {
      const { data } = await api.post(
        `/coachees/${coacheeId}/coach-reports/${report.id}/message`,
        { content: text }
      );
      setDraft("");
      onChange(data);
      // If the AI produced the full markdown, jump straight to the viewer.
      if ((data.report_markdown || "").trim()) {
        await onReportReady(data);
        return;
      }
      setTimeout(() => inputRef.current?.focus(), 50);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-3 max-w-3xl relative" data-testid="coach-report-chat">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={sending}
          data-testid="coach-report-chat-back"
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 transition-colors disabled:text-zinc-300 disabled:cursor-not-allowed"
        >
          <ArrowLeft size={14} /> Terug naar verslagen
        </button>
        <div className="text-xs text-zinc-500" data-testid="coach-report-chat-period">
          Periode: {formatPeriod(report)}
        </div>
      </div>

      <div className="neuro-card-bold p-4 bg-white">
        <div className="flex items-center gap-2">
          <Sparkle size={18} weight="duotone" className="text-[#1E88E5]" />
          <div className="text-sm text-zinc-700">
            <strong>Verslag-assistent</strong> · vraagt je eerst wat voor verslag je wilt en
            genereert daarna een markdown-verslag voor {coacheeName || "de coachee"}.
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="neuro-card p-4 bg-white max-h-[480px] overflow-y-auto space-y-3" data-testid="coach-report-thread">
        {(report.messages || []).map((m, i) => (
          <ChatBubble key={`${i}-${m.ts || ""}`} role={m.role} content={m.content} />
        ))}
        {sending && !generating && (
          <div className="flex justify-start">
            <div className="bg-zinc-100 text-zinc-500 border border-zinc-200 px-3.5 py-2.5 rounded-sm text-sm italic">
              De assistent is aan het typen…
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="coach-report-chat-error">{error}</div>
      )}

      <form onSubmit={send} className="flex gap-2">
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
          }}
          rows={2}
          placeholder="Typ je antwoord…"
          data-testid="coach-report-chat-input"
          disabled={sending}
          className="flex-1 px-3 py-2 border-2 border-zinc-400 bg-white rounded-sm text-sm resize-none focus:outline-none focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/20 disabled:bg-zinc-50"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          data-testid="coach-report-chat-send"
          className="px-4 bg-zinc-900 text-white text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] transition-colors disabled:bg-zinc-400"
        >
          <PaperPlaneTilt size={18} />
        </button>
      </form>

      {generating && <GeneratingInlineBanner />}
    </div>
  );
}

function GeneratingInlineBanner() {
  return (
    <div
      className="max-w-2xl neuro-card-bold p-6 bg-white mt-4"
      data-testid="coach-report-generating-inline"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 relative w-10 h-10">
          <div className="absolute inset-0 border-2 border-zinc-200 rounded-full"></div>
          <div className="absolute inset-0 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="flex-1">
          <h3 className="font-display text-base font-bold tracking-tight">Verslag wordt opgesteld…</h3>
          <p className="text-sm text-zinc-600 mt-1 leading-relaxed">
            De AI verwerkt de activity log en schrijft het verslag uit. Dit duurt meestal 10 – 30 seconden.
          </p>
          <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
            💡 <strong>Je kunt gerust doorwerken.</strong> Klik naar een andere pagina — de AI werkt op de
            achtergrond door en het conceptverslag verschijnt hier zodra het klaar is. Rechtsonderin het scherm
            zie je de status.
          </p>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")}>
      <div
        className={
          "max-w-[85%] px-3.5 py-2.5 rounded-sm text-sm whitespace-pre-wrap leading-relaxed " +
          (isUser ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-900 border border-zinc-200")
        }
      >
        {isUser ? content : (
          <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight prose-p:my-2 prose-li:my-0">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function ReportView({ coacheeId, report, onChange, onBack, onReopen, onDelete }) {
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState("");
  const isFinal = report.status === "finalized";
  const isConcept = report.status === "concept";

  const handleFinalize = async () => {
    if (finalizing) return;
    setFinalizing(true);
    setError("");
    try {
      const { data } = await api.post(`/coachees/${coacheeId}/coach-reports/${report.id}/finalize`);
      onChange(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="space-y-3 max-w-3xl" data-testid="coach-report-view">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button"
          onClick={onBack}
          data-testid="coach-report-view-back"
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft size={14} /> Terug naar verslagen
        </button>
        <div className="flex gap-2 flex-wrap">
          {isConcept && (
            <button
              type="button"
              onClick={handleFinalize}
              disabled={finalizing}
              data-testid="coach-report-view-finalize"
              className="inline-flex items-center gap-2 bg-[#43A047] text-white px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-sm hover:bg-[#2E7D32] transition-colors disabled:bg-zinc-400"
            >
              <Lock size={14} /> {finalizing ? "Bezig…" : "Maak definitief"}
            </button>
          )}
          {isConcept && (
            <button
              type="button"
              onClick={onReopen}
              data-testid="coach-report-view-reopen"
              className="inline-flex items-center gap-2 border-2 border-zinc-300 text-zinc-800 px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-sm hover:bg-zinc-100 transition-colors"
            >
              <NotePencil size={14} /> Aanpassen via chat
            </button>
          )}
          {!isFinal && (
            <button
              type="button"
              onClick={onDelete}
              data-testid="coach-report-view-delete"
              aria-label="Verwijder verslag"
              className="p-1.5 text-zinc-500 hover:text-[#E53935] hover:bg-[#E53935]/5 rounded-sm transition-colors"
            >
              <Trash size={16} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="border border-[#E53935]/30 bg-[#E53935]/5 text-[#E53935] text-sm px-4 py-3 rounded-sm" data-testid="coach-report-view-error">{error}</div>
      )}

      {isConcept && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 rounded-sm flex items-start gap-2" data-testid="coach-report-concept-banner">
          <Info size={18} className="shrink-0 mt-0.5 text-amber-700" />
          <div>
            Dit verslag is een <strong>concept</strong>. Lees het door en pas waar nodig aan via
            &ldquo;Aanpassen via chat&rdquo;. Klik op &ldquo;Maak definitief&rdquo; zodra je tevreden
            bent — daarna kun je het verslag nog wel opnieuw aanpassen, maar wordt de datum vastgelegd.
          </div>
        </div>
      )}

      <div className="neuro-card p-6 bg-white">
        <div className="flex items-start gap-3 mb-4">
          {isFinal ? (
            <CheckCircle size={22} className="text-[#43A047] shrink-0 mt-0.5" />
          ) : (
            <NotePencil size={22} className="text-amber-600 shrink-0 mt-0.5" />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg font-bold tracking-tight">{report.title || "Verslag"}</h3>
            <div className="text-xs text-zinc-500 mt-1">
              {isFinal
                ? `Definitief gemaakt op ${new Date(report.finalized_at).toLocaleString()}`
                : `Concept opgesteld op ${new Date(report.concept_at || report.updated_at || report.created_at).toLocaleString()}`}
              {" · "}door {report.coach_name || "—"}
              {" · "}periode {formatPeriod(report)}
            </div>
          </div>
        </div>
        <div className="prose prose-sm max-w-none prose-zinc prose-headings:font-display prose-headings:tracking-tight" data-testid="coach-report-markdown">
          <ReactMarkdown>{report.report_markdown || ""}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

function formatPeriod(report) {
  const f = report.period_from ? new Date(report.period_from).toLocaleDateString() : "begin";
  const t = report.period_until ? new Date(report.period_until).toLocaleDateString() : "nu";
  return `${f} → ${t}`;
}
